/**
 * GORA — the descent.
 *
 * One custom element, no dependencies: <gora-journey> lets the chapters drive
 * the stage image, the altitude readout and the rail progress.
 *
 * The reveals, the deferred images, the header measurement and <gora-gallery>
 * used to live here too. They are site-wide and this file is not — it ships
 * with the journey section, which only the homepage has — so they moved to
 * assets/gora-site.js, which layout/theme.liquid loads on every page.
 *
 * No-ops under prefers-reduced-motion beyond setting the final state.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const NARROW = window.matchMedia('(max-width: 899px)');

/* -------------------------------------------------------------------------
   The descent
   ------------------------------------------------------------------------- */

class GoraJourney extends HTMLElement {
  connectedCallback() {
    this.chapters = [...this.querySelectorAll('[data-chapter]')];
    this.plates = [...this.querySelectorAll('[data-plate]')];
    this.ticks = [...this.querySelectorAll('[data-tick]')];
    this.altEl = this.querySelector('[data-altitude]');
    this.unitEl = this.querySelector('[data-unit]');
    this.placeEl = this.querySelector('[data-place]');
    this.fillEl = this.querySelector('[data-fill]');
    this.ringEls = [...this.querySelectorAll('[data-ring]')];

    if (!this.chapters.length) return;

    this.active = -1;
    this.tweenFrom = this.readAltitude(0);
    this.rafId = null;

    // Place the rail ticks at each stop's real share of the descent.
    this.layoutTicks();

    // A chapter takes over when it crosses a band, not a line, so short
    // chapters still get their turn. On a phone the pinned image band covers
    // the top 40% of the screen, so the band has to sit lower — otherwise the
    // readout describes a chapter that is still hidden behind the picture.
    this.watch();
    NARROW.addEventListener('change', () => this.watch());
    this.setActive(0, true);

    // Nothing beyond the first plate is fetched until the descent is actually
    // approaching. A visitor who reads the hero and leaves pays for one image.
    this.armed = false;
    this.arm = new IntersectionObserver(
      (entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect();
        this.armed = true;
        this.hydrateAround(this.active);
      },
      { rootMargin: '900px 0px 900px 0px', threshold: 0 }
    );
    this.arm.observe(this);

    // Backstop: once the page has gone quiet, warm the first plate anyway. It
    // costs nothing at load time — the hero has already painted — and it means
    // a reader who scrolls straight down never meets an empty stage.
    const warm = () => {
      this.armed = true;
      this.hydrate(0);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 4000 });
    } else {
      setTimeout(warm, 2000);
    }
  }

  /**
   * Bring a deferred plate's URLs out of data- attributes. Called one and two
   * chapters ahead of the reader, so a plate is always decoded before its
   * cross-fade starts and nothing is fetched for a visitor who never scrolls
   * this far.
   */
  hydrateAround(index) {
    if (!this.armed) return;
    this.hydrate(index);
    this.hydrate(index + 1);
    this.hydrate(index + 2);
  }

  hydrate(index) {
    const img = this.plates[index]?.querySelector('img[data-src]');
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  }

  watch() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
      rootMargin: NARROW.matches ? '-58% 0px -28% 0px' : '-45% 0px -45% 0px',
      threshold: 0,
    });
    this.chapters.forEach((c) => this.observer.observe(c));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.arm?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  readAltitude(i) {
    return Number(this.chapters[i]?.dataset.altitude ?? 0);
  }

  /**
   * One tick per stop, evenly spaced. Spacing them by elevation instead reads
   * well for the three mountain stops and then stacks the two sea-level stops
   * on top of each other — the rail measures the journey, not the mountain.
   */
  layoutTicks() {
    const last = this.ticks.length - 1 || 1;
    this.ticks.forEach((tick, i) => {
      tick.style.insetBlockStart = `${(i / last) * 100}%`;
    });
  }

  onIntersect(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.chapters.indexOf(entry.target);
      if (index !== -1 && index !== this.active) this.setActive(index);
    }
  }

  setActive(index, immediate = false) {
    const previous = this.active;
    this.active = index;
    const chapter = this.chapters[index];

    this.hydrateAround(index);

    this.chapters.forEach((c, i) => c.setAttribute('data-active', String(i === index)));
    this.plates.forEach((p, i) => p.setAttribute('data-active', String(i === index)));
    this.ticks.forEach((t, i) => t.setAttribute('data-reached', String(i <= index)));

    if (this.placeEl) this.placeEl.textContent = chapter.dataset.place || '';
    // The unit changes halfway down: metres of descent, then kilometres across.
    if (this.unitEl) this.unitEl.textContent = chapter.dataset.unit || 'm';

    // Rail fill runs top-to-bottom as we descend.
    const progress = this.chapters.length > 1 ? (index / (this.chapters.length - 1)) * 100 : 100;
    if (this.fillEl) this.fillEl.style.setProperty('--gora-progress', `${progress}%`);

    // Contour rings widen as altitude drops — the land flattening out.
    this.ringEls.forEach((ring, i) => {
      const scale = 1 + (progress / 100) * (0.5 + i * 0.22);
      ring.style.setProperty('--gora-ring-scale', scale.toFixed(3));
    });

    // Counting 1,500 up to 11,400 would read as a climb. When the unit changes
    // the figure is a different measurement, so it snaps rather than tweens.
    const unitChanged =
      previous !== -1 && this.chapters[previous]?.dataset.unit !== chapter.dataset.unit;

    const to = this.readAltitude(index);
    if (immediate || unitChanged || REDUCED.matches) {
      // Cancel whatever is still in flight first. Without this a tween started
      // by the previous chapter keeps running and repaints its own figure a
      // frame after this one was set — so the readout showed "1,500 m" next to
      // "Wilmington, Delaware". Only reproducible when scrolling fast enough
      // that the 720ms tween outlives the chapter that began it.
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.paintAltitude(to);
      this.tweenFrom = to;
    } else {
      this.tweenAltitude(this.readAltitude(previous === -1 ? index : previous), to);
    }
  }

  paintAltitude(value) {
    if (this.altEl) this.altEl.textContent = Math.round(value).toLocaleString('en-US');
  }

  /** Ease the metres so the number reads as a descent, not a jump-cut. */
  tweenAltitude(from, to) {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    const duration = 720;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      this.paintAltitude(from + (to - from) * ease(t));
      if (t < 1) {
        this.rafId = requestAnimationFrame(step);
      } else {
        this.rafId = null;
        this.tweenFrom = to;
      }
    };
    this.rafId = requestAnimationFrame(step);
  }
}

if (!customElements.get('gora-journey')) {
  customElements.define('gora-journey', GoraJourney);
}
